// src/components/XbidForecastPanel.jsx
//
// Phase 2 (price-forecast expansion): the GR XBID (continuous intraday) per-MTU
// forecast panel for the Market -> Energy prices tab, shown next to the XBID
// actuals chart. Self-contained: fetches /xbid/forecast/latest itself.
//
// Three things are shown here that no other forecast panel shows, and each is
// a consequence of what the reviewed backtest actually measured:
//
//   1. THE POINT FORECAST IS A BASELINE, NOT THE GBM. The walk-forward backtest
//      compared them on the same rows and the GBM did not win on MAE, so under
//      the platform's "honest models or none" rule the reviewed baseline ships
//      as the recommended P50 and the GBM ships only as the calibrated band.
//      The panel says so in plain words instead of drawing a "model" line and
//      letting the viewer assume it is the machine-learned one.
//   2. THE RECOMMENDED P50 CAN SIT OUTSIDE THE BAND. Two different models, so
//      it is not constrained to lie inside. It is rare (99.3% inside in the
//      reviewed run), and when it happens the panel labels the count rather
//      than hiding it or clipping the line.
//   3. COVERAGE IS PART OF THE FORECAST. XBID publishes under a
//      minimum-coverage gate (>= 90 of 96 MTUs), not an all-or-nothing one,
//      because DAM has real gaps and some MTUs genuinely do not trade. A day
//      published with 93 of 96 periods says "93 of 96", never a silent chart.
//   4. THE VINTAGE LADDER, AND ITS PRICE. The same delivery day is forecast
//      more than once as information arrives - early at ~17:15 D-1 knowing
//      IDA1, late at 23:20 knowing IDA2 as well. The early rung is measurably
//      WORSE (13.32 vs 11.69 EUR/MWh MAE on the same 17,988 backtest rows) and
//      exists because a better forecast the user cannot act on before the IDA2
//      gate closes is worth nothing. The panel shows both rungs side by side
//      with that cost stated, so choosing the earlier one is an informed
//      choice rather than an invisible downgrade.
//
// The ladder strip degrades quietly: an older backend with no /ladder endpoint
// simply renders the panel as it was, on the late rung.
//
// Theme: MUI tokens for chrome (text.secondary, Paper background); fixed series
// hexes for chart marks, matching BalancingForecastPanel and the other market
// panels, chosen to read on both light and dark.
import React, { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import {
  Area,
  ComposedChart,
  CartesianGrid,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { fetchXbidForecastLadder, fetchXbidForecastLatest } from "../services/api";
import PublicationNote from "./PublicationNote";
import MarketInfoDialog from "./MarketInfoDialog";
import AppDatePicker from "./common/AppDatePicker";
import { chartTooltipProps } from "./common/chartTooltipStyles";

const COLORS = {
  band: "#1565c0",
  p50: "#1565c0",
  recommended: "#ef6c00",
};

const DEFAULT_VINTAGE = "late";
const EMPTY_ROWS = [];
const EMPTY_RUNGS = [];

function fmtMae(value) {
  return value === null || value === undefined ? null : `${Number(value).toFixed(2)}`;
}

// Human labels for the reviewed baselines that can be recommended. Unknown
// keys fall through to the raw model name - never renamed, never hidden.
//
// `ida_eff` MEANS SOMETHING DIFFERENT PER RUNG, which is why the label takes
// the anchor: on the late rung it is "IDA2, or IDA1 where IDA2 is absent"; on
// the early rung IDA2 has not cleared, so it is IDA1 and nothing else. The two
// rungs can therefore recommend a model with the same NAME and a different
// meaning, and a fixed label would quietly misdescribe the early one.
const MODEL_LABELS = {
  gbm: "GBM",
  ida_eff: "IDA effective",
  ida_eff_plus_prem14: "IDA effective + 14-day premium",
  ida_eff_plus_prem30: "IDA effective + 30-day premium",
  dam_hour_flat: "DAM hourly price, flat across the hour",
};

function anchorPhrase(idaAnchor) {
  return idaAnchor === "ida1"
    ? "IDA1 only - IDA2 has not cleared at this rung's run time"
    : "IDA2, or IDA1 where IDA2 is absent";
}

function defaultDeliveryDate() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

// One rung of the ladder. Deliberately shows THREE numbers rather than one:
// when it publishes, what it scored in the backtest (comparable across rungs),
// and what it is scoring live (only comparable once enough days exist, which
// is why the day count is always next to it).
function LadderRung({ rung, selected, onSelect }) {
  const backtest = fmtMae(rung.backtest_mae);
  const live = fmtMae(rung.live_mae);
  return (
    <ButtonBase
      onClick={() => onSelect(rung.vintage)}
      sx={{
        flex: "1 1 200px",
        minWidth: 190,
        textAlign: "left",
        justifyContent: "flex-start",
        alignItems: "stretch",
        borderRadius: 2,
        border: "1px solid",
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: selected ? "action.selected" : "transparent",
        px: 1.5,
        py: 1,
      }}
    >
      <Stack spacing={0.25} sx={{ width: "100%" }}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {rung.label}
          </Typography>
          {rung.nominal_run_local_time && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {rung.nominal_run_local_time} D-1
            </Typography>
          )}
          {!rung.published && (
            <Chip size="small" variant="outlined" color="default" label="not published" />
          )}
        </Stack>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {backtest ? `backtest MAE ${backtest}` : "backtest MAE not measured"}
          {live ? ` · live ${live} over ${rung.live_days}d` : " · no live score yet"}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          knows {rung.ida_anchor === "ida1" ? "DAM + IDA1" : "DAM + IDA1 + IDA2"} · deadline{" "}
          {rung.gate_note}
        </Typography>
      </Stack>
    </ButtonBase>
  );
}

export default function XbidForecastPanel({ market }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [deliveryDate, setDeliveryDate] = useState(defaultDeliveryDate);
  const [vintage, setVintage] = useState(DEFAULT_VINTAGE);
  const [payload, setPayload] = useState(null);
  const [ladder, setLadder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!market || !deliveryDate) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchXbidForecastLatest({ market, deliveryDate, vintage })
      .then((data) => {
        if (!cancelled) setPayload(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setPayload(null);
          setError(err?.message || "Failed to load XBID forecast");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [market, deliveryDate, vintage]);

  // The ladder is supplementary: a backend without the endpoint, or a market
  // without one, must not turn into an error banner over a working forecast.
  useEffect(() => {
    if (!market || !deliveryDate) return undefined;
    let cancelled = false;
    fetchXbidForecastLadder({ market, deliveryDate })
      .then((data) => {
        if (!cancelled) setLadder(data);
      })
      .catch(() => {
        if (!cancelled) setLadder(null);
      });
    return () => {
      cancelled = true;
    };
  }, [market, deliveryDate]);

  const run = payload?.run || null;
  const rows = payload?.rows || EMPTY_ROWS;
  const rungs = ladder?.rungs || EMPTY_RUNGS;
  // Only worth a strip once there is more than one rung to choose between.
  const showLadder = rungs.length > 1;
  const selectedRung = rungs.find((r) => r.vintage === vintage) || null;
  const bestRung = useMemo(() => {
    const measured = rungs.filter((r) => r.backtest_mae !== null && r.backtest_mae !== undefined);
    if (!measured.length) return null;
    return measured.reduce((a, b) => (b.backtest_mae < a.backtest_mae ? b : a));
  }, [rungs]);

  const chartData = useMemo(
    () =>
      rows.map((row) => ({
        time: (row.delivery_local_time || "").slice(0, 5),
        band: [row.p10, row.p90],
        p50: row.p50,
        recommended: row.recommended_p50,
      })),
    [rows]
  );

  const recommendedModel = run?.recommended_model || null;
  const recommendedIsBaseline = Boolean(recommendedModel) && recommendedModel !== "gbm";
  const recommendedLabel = recommendedModel
    ? MODEL_LABELS[recommendedModel] || recommendedModel
    : null;

  const outsideBandCount = useMemo(() => {
    if (!rows.length) return 0;
    return rows.filter((row) => row.recommended_within_band === false).length;
  }, [rows]);

  const shortfall = run ? run.period_shortfall : 0;

  // How much of this run leaned on the IDA1 fallback because IDA2 had not
  // cleared. null means the run predates the measurement (before 2026-08-27) -
  // UNKNOWN, never zero, so no clean badge is drawn over a run that simply
  // never recorded it.
  const anchorRows = run?.anchor_rows ?? null;
  const anchorFallback = run?.anchor_fallback_rows ?? null;
  const anchorKnown = anchorRows !== null && anchorFallback !== null && anchorRows > 0;
  const anchorFallbackShare = anchorKnown ? anchorFallback / anchorRows : null;
  const anchorDegraded = anchorKnown && anchorFallback > 0 && vintage === DEFAULT_VINTAGE;

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 3, mt: 3 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Continuous Intraday (XBID) Forecast
          </Typography>
          {showLadder && selectedRung && (
            <Chip
              size="small"
              color="primary"
              variant="filled"
              label={`${selectedRung.label}${
                selectedRung.nominal_run_local_time ? ` · ${selectedRung.nominal_run_local_time} D-1` : ""
              }`}
            />
          )}
          {recommendedModel && (
            <Chip
              size="small"
              variant="outlined"
              color={recommendedIsBaseline ? "warning" : "primary"}
              label={
                recommendedIsBaseline
                  ? `recommended: ${recommendedModel} (baseline)`
                  : "recommended: GBM"
              }
            />
          )}
          {run && shortfall > 0 && (
            <Chip
              size="small"
              variant="outlined"
              color="default"
              label={`${run.output_period_count} of ${run.expected_period_count} MTUs`}
            />
          )}
          {anchorDegraded && (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              label={`IDA1 fallback on ${anchorFallback}/${anchorRows}`}
            />
          )}
        </Stack>
        {/* Was <TextField type="date" />, which renders the browser-native
            picker - a different control from the one every other dashboard
            uses, and locale-dependent (a Greek user was shown mm/dd/yyyy).
            AppDatePicker keeps the same "YYYY-MM-DD" string contract, so the
            state and the handler are untouched; only the element changes. */}
        <AppDatePicker
          label="Delivery day"
          value={deliveryDate}
          onChange={setDeliveryDate}
          sx={{ minWidth: 170 }}
        />
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="flex-start">
        <PublicationNote product="xbid_forecast" market={market} />
        <MarketInfoDialog market={market} />
      </Stack>
      {showLadder && (
        <Box sx={{ mt: 1.5, mb: 1.5 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {rungs.map((rung) => (
              <LadderRung
                key={rung.vintage}
                rung={rung}
                selected={rung.vintage === vintage}
                onSelect={setVintage}
              />
            ))}
          </Stack>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.75 }}>
            The same delivery day, forecast more than once as information arrives. Backtest MAE is
            measured for every rung on the same {ladder?.backtest_basis?.shared_rows?.toLocaleString?.() ||
              ladder?.backtest_basis?.shared_rows}{" "}
            rows ({ladder?.backtest_basis?.window}), so the rungs are comparable to each other; the
            live figure is whatever each rung has actually been scored on so far.
            {bestRung && selectedRung && selectedRung.vintage !== bestRung.vintage &&
             selectedRung.backtest_mae !== null && selectedRung.backtest_mae !== undefined
              ? ` Showing the ${selectedRung.label.toLowerCase()} rung costs ` +
                `${(selectedRung.backtest_mae - bestRung.backtest_mae).toFixed(2)} EUR/MWh of accuracy ` +
                `against ${bestRung.label.toLowerCase()}, and buys the time to act on it.`
              : ""}
          </Typography>
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ height: 360, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={28} />
        </Box>
      ) : !run || chartData.length === 0 ? (
        <Alert severity="info">
          No published {vintage === DEFAULT_VINTAGE ? "" : `${vintage} `}XBID forecast for {market}{" "}
          on {deliveryDate}.{" "}
          {selectedRung?.nominal_run_local_time
            ? `This rung runs ~${selectedRung.nominal_run_local_time} Europe/Athens on D-1 and must publish by ${selectedRung.gate_note}.`
            : "The forecast runs ~23:20 Europe/Athens on D-1, after the IDA2 auction clears and before the delivery day begins."}{" "}
          Only Greece has a continuous-intraday data source today.
        </Alert>
      ) : (
        <>
          <Box sx={{ height: 360 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.35} />
                <XAxis dataKey="time" minTickGap={28} tick={{ fontSize: 12 }} />
                <YAxis
                  tick={{ fontSize: 12 }}
                  width={64}
                  label={{
                    value: "EUR/MWh",
                    angle: -90,
                    position: "insideLeft",
                    style: { fontSize: 11 },
                  }}
                />
                <Tooltip
                  {...chartTooltipProps(isDark)}
                  formatter={(value, name) => {
                    if (Array.isArray(value)) {
                      return [
                        `${Number(value[0]).toFixed(2)} – ${Number(value[1]).toFixed(2)}`,
                        "P10–P90",
                      ];
                    }
                    return [Number(value).toFixed(2), name];
                  }}
                  labelFormatter={(label) => `Delivery ${deliveryDate} ${label} (market-local)`}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="band"
                  name="P10–P90 (GBM+CQR)"
                  stroke="none"
                  fill={COLORS.band}
                  fillOpacity={0.14}
                  isAnimationActive={false}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey="p50"
                  name="P50 (GBM band centre)"
                  stroke={COLORS.p50}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
                {recommendedIsBaseline && (
                  <Line
                    type="monotone"
                    dataKey="recommended"
                    name={`P50 (recommended: ${recommendedModel})`}
                    stroke={COLORS.recommended}
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    dot={false}
                    isAnimationActive={false}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.75 }}>
            {recommendedIsBaseline
              ? `Per the reviewed walk-forward backtest, the ${recommendedLabel} baseline beats the GBM on ` +
                "mean absolute error for GR continuous intraday, so it is the recommended P50 (dashed); the " +
                "shaded band is the GBM's split-CQR calibrated uncertainty (target coverage 80%), shown for context." +
                (recommendedModel.startsWith("ida_eff") && selectedRung?.ida_anchor
                  ? ` On this rung "IDA effective" means ${anchorPhrase(selectedRung.ida_anchor)}.`
                  : "")
              : "GBM P50 with split-CQR calibrated P10–P90 band (target coverage 80%), per the reviewed walk-forward backtest."}
            {outsideBandCount > 0
              ? ` ${outsideBandCount} of ${rows.length} recommended values fall outside the band - expected, since the ` +
                "band and the point forecast come from different models."
              : ""}
            {shortfall > 0
              ? ` ${run.output_period_count} of ${run.expected_period_count} MTUs published (minimum ` +
                `${run.min_required_period_count}); the missing periods had no complete feature row - nothing was imputed.`
              : ""}
            {anchorDegraded
              ? ` ${anchorFallback} of ${anchorRows} periods fell back to IDA1 because IDA2 had not ` +
                `cleared for them, so ${anchorFallbackShare >= 0.5 ? "most" : "part"} of this forecast ` +
                "carries the same information the early rung already had - it is not the full late " +
                "vintage despite the label."
              : ""}
            {run.run_at
              ? ` Published ${new Date(run.run_at).toLocaleString("en-GB")}` +
                (showLadder && selectedRung ? ` (${selectedRung.label.toLowerCase()} rung).` : ".")
              : ""}
          </Typography>
        </>
      )}
    </Paper>
  );
}
